import React, { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useSEO } from '../hooks/useSEO';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const badgeShimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
`;

const PageWrapper = styled.div`
  background: linear-gradient(180deg, #fefcf8 0%, #f8efe6 46%, #faf3ec 100%);
  padding-bottom: clamp(80px, 14vw, 160px);
  overflow-x: hidden;
`;

const HeroBackdrop = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(110px, 18vw, 180px) 0 clamp(96px, 12vw, 150px);
  background:
    radial-gradient(circle at 12% 18%, rgba(108, 129, 117, 0.08), transparent 58%),
    radial-gradient(circle at 82% 12%, rgba(215, 186, 155, 0.17), transparent 58%),
    radial-gradient(circle at 18% 88%, rgba(171, 146, 128, 0.12), transparent 54%),
    linear-gradient(165deg, #fefbf7 0%, #f8eee4 45%, #fbf4ed 100%);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(182deg, rgba(255, 254, 252, 0.5) 0%, rgba(255, 255, 255, 0) 22%),
      radial-gradient(circle at 6% 93%, rgba(208, 184, 161, 0.08) 0%, transparent 52%),
      radial-gradient(circle at 94% 4%, rgba(168, 180, 168, 0.05) 0%, transparent 48%);
  }

  @media (max-width: 768px) {
    padding: 96px 0 80px;
  }
`;

const HeroInner = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: clamp(78px, 8vw, 116px);
  max-width: clamp(1000px, 78vw, 1120px);
  margin: 0 auto;
  width: 100%;
  padding: 0 clamp(30px, 4.5vw, 56px);

  @media (min-width: 1400px) {
    max-width: clamp(1080px, 72vw, 1180px);
    gap: clamp(82px, 7.5vw, 112px);
  }

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: center;
    gap: clamp(36px, 9vw, 52px);
  }
`;

const HeroContent = styled.div`
  display: grid;
  gap: clamp(22px, 3.6vw, 32px);
  max-width: clamp(420px, 40vw, 460px);
  text-align: left;
  z-index: 2;
  animation: ${fadeUp} 520ms ease-out 90ms both;

  @media (min-width: 1101px) {
    justify-items: start;
    padding-right: clamp(32px, 3.8vw, 48px);
  }

  @media (max-width: 768px) {
    text-align: center;
    justify-items: center;
    width: 100%;
    padding: 0 clamp(12px, 6vw, 22px);
  }

  @media (max-width: 1100px) {
    max-width: 520px;
  }
`;

const HeroTag = styled.span`
  width: fit-content;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(70, 83, 75, 0.12);
  color: rgba(45, 56, 50, 0.82);
  font-weight: 600;
  font-size: clamp(0.82rem, 0.9vw, 0.92rem);
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.32rem, 3vw, 2.94rem);
  font-weight: 630;
  color: #2b2725;
  line-height: clamp(1.1, 1.35vw, 1.16);
  letter-spacing: -0.008em;
  max-width: 22ch;
`;

const HeroSubtitle = styled.span`
  font-size: clamp(1rem, 2.2vw, 1.22rem);
  font-weight: 600;
  color: rgba(43, 41, 39, 0.78);
  font-style: italic;

  @media (max-width: 768px) {
    max-width: 24ch;
  }
`;

const HeroText = styled.p`
  font-size: clamp(1.05rem, 2.1vw, 1.24rem);
  line-height: 1.92;
  color: rgba(43, 41, 39, 0.72);
  max-width: 520px;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(14px, 3vw, 18px);
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const PrimaryAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: clamp(14px, 2.9vw, 18px) clamp(26px, 4.4vw, 36px);
  border-radius: 999px;
  background: linear-gradient(145deg, #2f3a34 0%, #4a5a52 100%);
  color: #f8f3ed;
  font-weight: 600;
  text-decoration: none;
  font-size: clamp(1rem, 2vw, 1.06rem);
  box-shadow: 0 18px 34px rgba(22, 28, 24, 0.28);
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 26px 44px rgba(22, 28, 24, 0.32);
    background: linear-gradient(145deg, #36453f 0%, #53635b 100%);
  }
`;

const SecondaryAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: clamp(14px, 2.8vw, 18px) clamp(22px, 3.8vw, 32px);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #2f3a34;
  font-weight: 600;
  text-decoration: none;
  font-size: clamp(1rem, 1.9vw, 1.04rem);
  border: 1px solid rgba(222, 210, 198, 0.58);
  box-shadow: 0 16px 36px rgba(22, 18, 14, 0.12);
  transition: transform 0.22s ease, box-shadow 0.22s ease, color 0.22s ease, background 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 22px 44px rgba(22, 18, 14, 0.18);
    background: rgba(248, 243, 237, 0.94);
    color: #2b2725;
  }
`;

const HeroHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: clamp(18px, 3.2vw, 24px);
  justify-items: center;
  width: 100%;
  animation: ${fadeUp} 520ms ease-out 160ms both;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: clamp(14px, 5vw, 18px);
  }
`;

const HighlightCard = styled.div`
  padding: clamp(16px, 3vw, 22px);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(222, 210, 198, 0.34);
  box-shadow: 0 12px 28px rgba(40, 35, 30, 0.07);
  display: grid;
  gap: 6px;
  width: 100%;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const HighlightTitle = styled.span`
  font-weight: 600;
  color: #2e3933;
  font-size: 1rem;
`;

const HighlightDescription = styled.span`
  color: rgba(46, 57, 51, 0.7);
  font-size: 0.94rem;
  line-height: 1.6;
`;

const HeroMedia = styled.div`
  position: relative;
  display: grid;
  gap: clamp(28px, 4.8vw, 36px);
  justify-items: stretch;
  max-width: clamp(400px, 38vw, 460px);
  width: 100%;
  margin: 0;
  align-self: center;
  animation: ${fadeUp} 560ms ease-out 210ms both;

  @media (min-width: 1101px) {
    margin-left: clamp(28px, 5vw, 52px);
    align-self: stretch;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin: 0 auto;
  }
`;

const HeroVisualGroup = styled.section`
  display: grid;
  justify-items: center;
  gap: clamp(22px, 4.4vw, 32px);
  text-align: center;
`;

const HeroVisualTagline = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: clamp(4px, 1.1vw, 8px);
  padding: clamp(6px, 1.3vw, 9px) clamp(18px, 2.6vw, 24px);
  border-radius: 999px;
  background: rgba(252, 248, 242, 0.92);
  color: rgba(75, 70, 65, 0.9);
  font-size: clamp(0.74rem, 1.1vw, 0.88rem);
  font-weight: 600;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  box-shadow: 0 14px 28px rgba(160, 138, 110, 0.18);
`;

const HeroPrimary = styled.figure`
  position: relative;
  border-radius: clamp(36px, 5.2vw, 56px);
  overflow: hidden;
  padding: clamp(18px, 3.6vw, 26px);
  display: grid;
  place-items: center;
  width: min(100%, clamp(320px, 36vw, 398px));
  aspect-ratio: 4 / 3;
  transform: translate3d(var(--tiltX, 0px), var(--tiltY, 0px), 0) scale(var(--tiltScale, 1));
  box-shadow:
    0 12px 34px -18px rgba(112, 82, 48, 0.26);
  background: #fffaf6;
  border: 2px solid rgba(231, 217, 200, 0.8);
  transition:
    transform 600ms cubic-bezier(0.19, 1, 0.22, 1),
    box-shadow 600ms cubic-bezier(0.19, 1, 0.22, 1),
    border-color 600ms cubic-bezier(0.19, 1, 0.22, 1);
  will-change: transform, box-shadow;

  &::before {
    content: '';
    position: absolute;
    inset: clamp(10px, 2.6vw, 18px);
    border-radius: inherit;
    background: rgba(255, 255, 255, 0.45);
    mix-blend-mode: lighten;
    pointer-events: none;
    z-index: 0;
  }

  &:hover {
    --tiltScale: 1.015;
    box-shadow:
      0 20px 45px -20px rgba(180, 140, 80, 0.28);
    border-color: rgba(236, 222, 206, 0.95);
  }

  @media (max-width: 768px) {
    padding: clamp(24px, 7vw, 32px);
    transform: translate3d(0, 0, 0) scale(1);
  }
`;

const HeroPrimaryScene = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  border-radius: clamp(24px, 4vw, 34px);
  overflow: hidden;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.65), rgba(247, 236, 224, 0.28) 55%, transparent 90%);
  z-index: 2;
  transition: transform 600ms cubic-bezier(0.19, 1, 0.22, 1);

  ${HeroPrimary}:hover & {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    transform: none !important;
  }
`;

const HeroPrimaryImage = styled.img`
  position: relative;
  z-index: 3;
  width: 110%;
  height: 110%;
  object-fit: cover;
  filter: brightness(1.1) contrast(0.9) saturate(0.9) hue-rotate(10deg) sepia(0.1);
  transition:
    transform 600ms cubic-bezier(0.19, 1, 0.22, 1),
    filter 600ms cubic-bezier(0.19, 1, 0.22, 1);

  ${HeroPrimary}:hover & {
    transform: translateY(-3px) scale(1.02);
    filter: brightness(1.1) contrast(0.9) saturate(0.9) hue-rotate(10deg) sepia(0.1);
  }
`;

const HeroPrimarySunlight = styled.span`
  position: absolute;
  inset: -45% 45% 55% -45%;
  background: radial-gradient(circle at top left, rgba(255, 253, 248, 0.8), rgba(255, 245, 235, 0.22) 45%, transparent 68%);
  mix-blend-mode: screen;
  pointer-events: none;
  z-index: 1;
`;

const HeroInfoCard = styled.div`
  position: relative;
  display: grid;
  gap: clamp(12px, 2vw, 20px);
  padding: clamp(26px, 3.6vw, 36px);
  border-radius: clamp(30px, 4.2vw, 40px);
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(224, 206, 188, 0.56);
  box-shadow: 0 32px 52px -28px rgba(42, 34, 26, 0.22);
  animation: ${fadeUp} 560ms ease-out 240ms both;
  max-width: clamp(340px, 40vw, 380px);
  width: 100%;
`;

const HeroPrimaryHeading = styled.span`
  font-size: clamp(1.22rem, 2.4vw, 1.42rem);
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #2f3a34;
`;

const HeroPrimaryText = styled.p`
  margin: 0;
  font-size: clamp(1.02rem, 2.1vw, 1.18rem);
  line-height: 1.8;
  color: rgba(44, 42, 41, 0.72);
  max-width: 36ch;
`;

const HeroBadge = styled.span`
  position: absolute;
  top: clamp(14px, 2.6vw, 24px);
  left: clamp(26px, 4vw, 38px);
  padding: clamp(4px, 1.1vw, 7px) clamp(16px, 2.4vw, 20px);
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 247, 234, 0.98) 45%, rgba(255, 255, 255, 0.98) 100%);
  background-size: 220% 220%;
  border: 1px solid rgba(220, 208, 194, 0.6);
  color: rgba(72, 68, 60, 0.9);
  font-weight: 600;
  font-size: clamp(0.62rem, 1vw, 0.82rem);
  letter-spacing: 0.26em;
  text-transform: uppercase;
  box-shadow: 0 12px 24px rgba(231, 209, 176, 0.4);
  z-index: 4;
  transform: translateY(-46%);
  transition: transform 260ms ease, box-shadow 280ms ease, background-position 1.5s linear;

  ${HeroPrimary}:hover & {
    transform: translateY(-52%);
    box-shadow: 0 16px 34px rgba(231, 209, 176, 0.48);
    background-position: 200% 50%;
    animation: ${badgeShimmer} 1.5s linear infinite;
  }
`;

const HeroSecondary = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(20px, 3.6vw, 28px);
  width: 100%;
  padding: clamp(20px, 3.4vw, 26px);
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(248, 244, 238, 0.92), rgba(255, 255, 255, 0.94));
  border: 1px solid rgba(220, 211, 201, 0.34);
  box-shadow: 0 12px 28px rgba(40, 35, 30, 0.07);
  align-self: center;
`;

const HeroSecondaryIcon = styled.span`
  font-size: clamp(2rem, 4vw, 2.4rem);
  filter: saturate(1.1);
`;

const HeroSecondaryContent = styled.div`
  display: grid;
  gap: clamp(4px, 1vw, 10px);
`;

const HeroSecondaryTitle = styled.span`
  font-weight: 600;
  font-size: clamp(1rem, 2vw, 1.06rem);
  color: #2f3a34;
`;

const HeroSecondaryText = styled.p`
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.64;
  color: rgba(47, 58, 52, 0.7);
`;

const MainContainer = styled.div`
  max-width: clamp(1000px, 78vw, 1120px);
  margin: 0 auto;
  width: 100%;
  padding: 0 clamp(30px, 4.5vw, 56px);

  @media (max-width: 768px) {
    padding: 0 clamp(16px, 6vw, 28px);
  }
`;

const SectionSpacing = styled.section`
  margin-top: clamp(70px, 9vw, 110px);
`;

const SectionIntro = styled.div`
  display: grid;
  gap: clamp(18px, 3vw, 26px);
  max-width: 780px;
  margin-bottom: clamp(36px, 5vw, 54px);

  @media (max-width: 768px) {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }
`;

const SectionEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(47, 58, 52, 0.7);

  &::before {
    content: '';
    display: inline-block;
    width: 34px;
    height: 1px;
    background: rgba(47, 58, 52, 0.4);
  }

  @media (max-width: 768px) {
    justify-content: center;
    margin: 0 auto;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 3vw, 2.6rem);
  font-weight: 600;
  color: #2f3a34;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const SectionText = styled.p`
  font-size: clamp(1rem, 1.9vw, 1.14rem);
  line-height: 1.8;
  color: rgba(44, 42, 41, 0.72);

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(22px, 5vw, 32px);
  justify-items: stretch;

  @media (max-width: 768px) {
    gap: clamp(20px, 6vw, 26px);
  }
`;

const ServiceCard = styled.article<{ accent?: string }>`
  position: relative;
  border-radius: 28px;
  background: ${({ accent }) =>
    accent
      ? `linear-gradient(140deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.85) 38%, ${accent})`
      : 'rgba(255, 255, 255, 0.9)'};
  box-shadow: 0 30px 58px rgba(44, 35, 28, 0.12);
  border: 1px solid rgba(222, 211, 198, 0.4);
  overflow: hidden;
  display: grid;
  gap: 18px;
  padding: clamp(24px, 3vw, 32px);

  @media (max-width: 600px) {
    padding: 22px;
  }
`;

const ServiceIcon = styled.span`
  font-size: clamp(2rem, 4vw, 2.5rem);
  filter: saturate(1.1);
`;

const ServiceTitle = styled.h3`
  font-size: clamp(1.25rem, 2.2vw, 1.55rem);
  font-weight: 600;
  color: #2c2a29;
`;

const ServiceDescription = styled.p`
  color: rgba(44, 42, 41, 0.72);
  line-height: 1.7;
  font-size: 1rem;
  margin: 0;
`;

const StoryBlock = styled.section`
  margin-top: clamp(80px, 11vw, 130px);
  position: relative;
  border-radius: 32px;
  padding: clamp(32px, 5vw, 48px);
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 36px 64px rgba(38, 33, 29, 0.14);
  border: 1px solid rgba(223, 214, 203, 0.45);
  display: grid;
  gap: clamp(20px, 3vw, 28px);

  @media (max-width: 768px) {
    padding: clamp(26px, 6vw, 32px);
  }
`;

const StoryTitle = styled.h3`
  font-size: clamp(1.6rem, 2.4vw, 1.9rem);
  font-weight: 600;
  color: #2f3a34;
  letter-spacing: -0.01em;
`;

const StoryText = styled.p`
  font-size: clamp(1rem, 1.9vw, 1.14rem);
  line-height: 1.8;
  color: rgba(44, 42, 41, 0.72);
  margin: 0;
`;

const StoryHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: clamp(18px, 4vw, 26px);

  @media (max-width: 768px) {
    gap: clamp(16px, 6vw, 22px);
  }
`;

const StoryHighlight = styled.div`
  border-radius: 22px;
  padding: clamp(20px, 3.4vw, 26px);
  background: rgba(248, 243, 237, 0.82);
  border: 1px solid rgba(222, 211, 198, 0.4);
  box-shadow: 0 18px 36px rgba(40, 35, 30, 0.12);
  display: grid;
  gap: 10px;
`;

const StoryHighlightTitle = styled.span`
  font-weight: 600;
  color: #2f3a34;
  font-size: 1.02rem;
`;

const StoryHighlightText = styled.p`
  margin: 0;
  color: rgba(44, 42, 41, 0.72);
  font-size: 0.98rem;
  line-height: 1.6;
`;

const ContactSection = styled.section`
  margin-top: clamp(90px, 12vw, 150px);
  position: relative;
  overflow: hidden;
  padding: clamp(70px, 10vw, 110px) 0;
  background: radial-gradient(circle at 15% 20%, rgba(95, 109, 98, 0.12), transparent 55%),
    radial-gradient(circle at 85% 80%, rgba(209, 178, 149, 0.18), transparent 60%),
    linear-gradient(160deg, #2f3a34 0%, #1f2823 100%);
  color: #fff;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

const ContactCard = styled.div`
  display: grid;
  justify-items: center;
  text-align: center;
  gap: clamp(20px, 3vw, 28px);
  max-width: 620px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: clamp(18px, 6vw, 24px);
    padding: 0 16px;
  }
`;

const ContactHeading = styled.h2`
  font-size: clamp(2rem, 3.2vw, 2.6rem);
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const ContactText = styled.p`
  font-size: clamp(1.05rem, 2vw, 1.2rem);
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.82);
  margin: 0;

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ContactButtons = styled.div`
  display: flex;
  gap: clamp(14px, 3vw, 20px);
  flex-wrap: wrap;
  justify-content: center;
`;

const ContactPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 36px;
  border-radius: 999px;
  background: #f8f3ed;
  color: #2f3a34;
  font-weight: 600;
  text-decoration: none;
  font-size: 1.06rem;
  box-shadow: 0 24px 46px rgba(10, 14, 12, 0.22);
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 30px 56px rgba(10, 14, 12, 0.3);
    background: #ffffff;
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

const ContactSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  text-decoration: none;
  font-size: 1.02rem;
  border: 1px solid rgba(255, 255, 255, 0.24);
  box-shadow: 0 20px 40px rgba(10, 14, 12, 0.18);
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, color 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 28px 48px rgba(10, 14, 12, 0.26);
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

const Blombud: React.FC = () => {
  useSEO({
    entityType: 'page',
    entityId: 'blombud',
    fallbackTitle: 'Blombud - Skicka blommor via oss',
    fallbackDescription:
      'Skicka blommor till dina nära i Kramfors via Lilla Flora. Våra florister skapar vackra buketter med omsorg och kärlek.',
    fallbackImage: 'https://lillaflora.nu/og-blombud.jpg',
  });

  const featureData = useMemo(
    () => [
      {
        icon: '🚚',
        title: 'Hela Kramfors',
        description:
          'Genom vårt lokala nätverk levererar vi blommor i hela Kramfors, snabbt och säkert.',
        accent: 'rgba(209, 178, 149, 0.38)',
      },
      {
        icon: '🌹',
        title: 'Färska kvalitetsblommor',
        description:
          'Vi använder endast de färskaste blommorna och skapar varje bukett med omsorg för bästa kvalitet.',
        accent: 'rgba(182, 162, 143, 0.32)',
      },
      {
        icon: '💝',
        title: 'Personliga arrangemang',
        description:
          'Varje blombud anpassas efter dina önskemål och mottagarens personlighet för en unik upplevelse.',
        accent: 'rgba(160, 178, 168, 0.3)',
      },
      {
        icon: '📞',
        title: 'Personlig service',
        description:
          'Vårt team hjälper dig gärna att välja rätt blommor och se till att allt blir perfekt.',
      },
      {
        icon: '⏰',
        title: 'Snabb leverans',
        description:
          'Vi arbetar för att leverera ditt blombud så snabbt som möjligt, ofta samma dag som beställningen.',
      },
      {
        icon: '🏆',
        title: 'Lokal expertis',
        description:
          'Som en etablerad florist i Kramfors känner vi våra kunder och vet vad som gör dem glada.',
      },
    ],
    []
  );

  const heroHighlights = useMemo(() => featureData.slice(0, 3), [featureData]);
  const secondaryFeature = featureData[3];
  const storyHighlights = useMemo(() => featureData.slice(4, 6), [featureData]);

  const handleHeroMouseMove = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return;
    }

    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const offsetX = event.clientX - (rect.left + rect.width / 2);
    const offsetY = event.clientY - (rect.top + rect.height / 2);

    const tiltX = (offsetX / rect.width) * 6;
    const tiltY = (offsetY / rect.height) * 6;

    target.style.setProperty('--tiltX', `${tiltX.toFixed(2)}px`);
    target.style.setProperty('--tiltY', `${tiltY.toFixed(2)}px`);
  }, []);

  const handleHeroMouseLeave = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const target = event.currentTarget;
    target.style.setProperty('--tiltX', '0px');
    target.style.setProperty('--tiltY', '0px');
  }, []);

  return (
    <PageWrapper>
      <HeroBackdrop>
        <HeroInner>
          <HeroContent>
            <HeroTag>Blombud i Kramfors</HeroTag>
            <HeroTitle>Blombud</HeroTitle>
            <HeroSubtitle>"Du kan nu skicka blommor via oss!"</HeroSubtitle>
            <HeroText>
              Välkommen till Lilla Flora i Kramfors! Nu kan du enkelt skicka blommor till dina nära i Kramfors.
              Våra florister skapar vackra buketter med omsorg och kärlek. Tryck på knappen nedan för att skicka ditt
              blombud redan idag eller slå oss en signal!
            </HeroText>

            <HeroActions>
              <PrimaryAction to="/kontakt">Skicka ett blombud - kontakta oss</PrimaryAction>
              <SecondaryAction to="/produkter">🛍️ Se våra produkter</SecondaryAction>
            </HeroActions>

            <HeroHighlights>
              {heroHighlights.map((item) => (
                <HighlightCard key={item.title}>
                  <HighlightTitle>{item.title}</HighlightTitle>
                  <HighlightDescription>{item.description}</HighlightDescription>
                </HighlightCard>
              ))}
            </HeroHighlights>
          </HeroContent>

          <HeroMedia>
            <HeroVisualGroup>
              <HeroVisualTagline>Vi levererar blommor i hela Kramfors</HeroVisualTagline>
              <HeroPrimary onMouseMove={handleHeroMouseMove} onMouseLeave={handleHeroMouseLeave}>
                <HeroPrimarySunlight aria-hidden="true" />
                <HeroBadge>Lokala florister</HeroBadge>
                <HeroPrimaryScene>
                  <HeroPrimaryImage
                    src="/static/blombud_bil.png"
                    alt="Blombudbil från Lilla Flora utanför butiken"
                    loading="eager"
                    decoding="async"
                  />
                </HeroPrimaryScene>
              </HeroPrimary>
              <HeroInfoCard>
                <HeroPrimaryHeading>Skicka ett blombud - kontakta oss</HeroPrimaryHeading>
                <HeroPrimaryText>
                  Som din lokala florist i Kramfors ser vi till att varje bukett skapas med personlig omsorg och
                  levereras med kärlek här i Kramfors. Vi är stolta lokala florister i Kramfors.
                </HeroPrimaryText>
              </HeroInfoCard>
            </HeroVisualGroup>

            {secondaryFeature && (
              <HeroSecondary>
                <HeroSecondaryIcon>{secondaryFeature.icon}</HeroSecondaryIcon>
                <HeroSecondaryContent>
                  <HeroSecondaryTitle>{secondaryFeature.title}</HeroSecondaryTitle>
                  <HeroSecondaryText>{secondaryFeature.description}</HeroSecondaryText>
                </HeroSecondaryContent>
              </HeroSecondary>
            )}
          </HeroMedia>
        </HeroInner>
      </HeroBackdrop>

      <MainContainer>
        <SectionSpacing>
          <SectionIntro>
            <SectionEyebrow>Florister i Kramfors</SectionEyebrow>
            <SectionTitle>Blombud - skicka blommor via oss</SectionTitle>
            <SectionText>
              Vårt team hjälper dig gärna att välja rätt blommor och se till att allt blir perfekt. Vi arbetar för att
              leverera ditt blombud så snabbt som möjligt, ofta samma dag som beställningen.
            </SectionText>
          </SectionIntro>

          <ServicesGrid>
            {featureData.map((item) => (
              <ServiceCard key={item.title} accent={item.accent}>
                <ServiceIcon>{item.icon}</ServiceIcon>
                <ServiceTitle>{item.title}</ServiceTitle>
                <ServiceDescription>{item.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </SectionSpacing>

        <StoryBlock>
          <StoryTitle>Vi kör i hela Kramfors - välj oss Lillaflora i Kramfors</StoryTitle>
          <StoryText>
            Som din lokala florist i Kramfors ser vi till att varje bukett skapas med personlig omsorg och levereras
            med kärlek inom Kramfors. Vi är stolta lokala florister i Kramfors!
          </StoryText>

          <StoryHighlights>
            {storyHighlights.map((item) => (
              <StoryHighlight key={item.title}>
                <StoryHighlightTitle>
                  {item.icon} {item.title}
                </StoryHighlightTitle>
                <StoryHighlightText>{item.description}</StoryHighlightText>
              </StoryHighlight>
            ))}
          </StoryHighlights>
        </StoryBlock>
      </MainContainer>

      <ContactSection>
        <ContactCard>
          <ContactHeading>Skicka ett blombud - kontakta oss</ContactHeading>
          <ContactText>
            Tryck på knappen nedan för att skicka ditt blombud redan idag eller slå oss en signal!
          </ContactText>
          <ContactButtons>
            <ContactPrimary to="/kontakt">Skicka ett blombud - kontakta oss</ContactPrimary>
            <ContactSecondary to="/produkter">🛍️ Se våra produkter</ContactSecondary>
          </ContactButtons>
        </ContactCard>
      </ContactSection>
    </PageWrapper>
  );
};

export default Blombud;
